import { z } from 'zod';
import { SalesOrderShortfallResolution } from '../../constants/sales-order-shortfall-resolution.js';
import { isWholeQuantity } from '../../utils/is-whole-quantity.js';
import { WHOLE_QUANTITY_MESSAGE } from '../quantity/whole-quantity-message.js';
import { requiredUuidString } from '../zod-helpers/index.js';
import { shipLineCaptureInputSchema } from './ship-line-capture-input.js';
import { shipShortfallChoiceInputSchema } from './ship-shortfall-choice-input.js';
const shortfallRowSchema = z
    .object({
    salesOrderItemId: requiredUuidString('Line is required.', 'Invalid line selection.'),
    savedResolutionId: z.enum(SalesOrderShortfallResolution).nullable(),
    resolutionId: z.enum(SalesOrderShortfallResolution).nullable(),
})
    .superRefine((row, context) => {
    const result = shipShortfallChoiceInputSchema.shape.resolutionId.safeParse(row.resolutionId);
    const message = result.error?.issues[0]?.message;
    if (message)
        context.addIssue({ code: 'custom', path: ['resolutionId'], message });
});
const captureRowSchema = z
    .object({
    salesOrderItemId: requiredUuidString('Line is required.', 'Invalid line selection.'),
    shippedQty: z.number(),
    /** The line's or its part's unit is counted in whole pieces. */
    qtyIsIntegral: z.boolean(),
    qtyRejected: z
        .number({ error: 'Enter a rejected quantity of 0 or more.' })
        .min(0, 'Enter a rejected quantity of 0 or more.'),
    rejectionReason: z.string(),
})
    .superRefine((row, context) => {
    if (row.qtyRejected > row.shippedQty) {
        context.addIssue({
            code: 'custom',
            path: ['qtyRejected'],
            message: `Rejected can't be more than the ${row.shippedQty} shipped.`,
        });
    }
    if (row.qtyIsIntegral && !isWholeQuantity(row.qtyRejected)) {
        context.addIssue({ code: 'custom', path: ['qtyRejected'], message: WHOLE_QUANTITY_MESSAGE });
    }
    if (!(row.qtyRejected > 0))
        return;
    // The API's own quantity and reason rules, so the dialog refuses exactly what Ship would.
    const quantity = shipLineCaptureInputSchema.shape.qtyRejected.safeParse(row.qtyRejected);
    const quantityMessage = quantity.error?.issues[0]?.message;
    if (quantityMessage) {
        context.addIssue({ code: 'custom', path: ['qtyRejected'], message: quantityMessage });
    }
    const reason = shipLineCaptureInputSchema.shape.rejectionReason.safeParse(row.rejectionReason);
    const message = reason.error?.issues[0]?.message;
    if (message)
        context.addIssue({ code: 'custom', path: ['rejectionReason'], message });
});
/** The Shortfall step: one row per short line, preset with its saved choice (S-2). */
export const shipDialogShortfallStepSchema = z.array(shortfallRowSchema);
/** The Delivery Capture step: one row per shipped line; delivered is shipped minus rejected (S-3). */
export const shipDialogCaptureStepSchema = z.array(captureRowSchema);
export const shipDialogFormSchema = z.object({
    shortfall: shipDialogShortfallStepSchema,
    capture: shipDialogCaptureStepSchema,
});
//# sourceMappingURL=ship-dialog-form.js.map